export type CapacityRisk = "healthy" | "near" | "at";
export type ValidityRisk = "healthy" | "soon" | "expired";

export const VALIDITY_SOON_DAYS = 14;
export const SEAT_NEAR_CAPACITY_PERCENT = 90;

export function licenseSeatSnapshot(seatLimit: number, allocated: number, inUse: number) {
  const limit = Math.max(0, seatLimit);
  const used = Math.max(0, inUse);
  const issued = Math.max(0, allocated);
  const unallocated = Math.max(0, limit - issued);
  const allocatedIdle = Math.max(0, Math.min(issued, limit) - Math.min(used, limit));
  const overAllocated = Math.max(0, issued - limit);
  const utilization = limit === 0 ? (used > 0 ? 100 : 0) : Math.min(100, (used / limit) * 100);
  const risk: CapacityRisk = limit > 0 && used >= limit ? "at" : utilization >= SEAT_NEAR_CAPACITY_PERCENT ? "near" : "healthy";
  return {
    limit,
    used: Math.min(used, limit || used),
    issued,
    unallocated,
    allocatedIdle,
    overAllocated,
    utilization,
    risk,
    usedWidth: limit === 0 ? 0 : (Math.min(used, limit) / limit) * 100,
    idleWidth: limit === 0 ? 0 : (allocatedIdle / limit) * 100,
  };
}

export function licenseValiditySnapshot(
  validFrom: string,
  validUntil: string | null | undefined,
  now = Date.now(),
  timezone = "UTC",
) {
  const from = Date.parse(validFrom);
  const until = validUntil ? Date.parse(validUntil) : Number.NaN;
  if (!Number.isFinite(from)) return { kind: "unknown" as const };
  if (validUntil && !Number.isFinite(until)) return { kind: "unknown" as const };
  // Compare local calendar dates, so relative labels agree with the displayed dates across DST.
  const calendarOptions = { year: "numeric", month: "numeric", day: "numeric" } as const;
  let calendar: Intl.DateTimeFormat;
  try {
    calendar = new Intl.DateTimeFormat("en-US", { ...calendarOptions, timeZone: timezone });
  } catch {
    // Match the UTC fallback used by formatDateTimeWithTz for unsupported saved zones.
    calendar = new Intl.DateTimeFormat("en-US", { ...calendarOptions, timeZone: "UTC" });
  }
  const calendarDay = (value: number) => {
    const parts = calendar.formatToParts(value);
    const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((item) => item.type === type)?.value);
    return Date.UTC(part("year"), part("month") - 1, part("day")) / 86_400_000;
  };
  const today = calendarDay(now);
  const startsInDays = from > now ? calendarDay(from) - today : null;
  if (!Number.isFinite(until)) return { kind: "perpetual" as const, from, startsInDays };
  const remainingMs = until - now;
  const remainingDays = Math.ceil(remainingMs / 86_400_000);
  const risk: ValidityRisk = remainingMs <= 0 ? "expired" : remainingDays <= VALIDITY_SOON_DAYS ? "soon" : "healthy";
  return {
    kind: "finite" as const,
    from,
    until,
    startsInDays,
    calendarDaysRemaining: calendarDay(until) - today,
    remainingMs,
    remainingDays,
    risk,
  };
}
